import type {
	PrefetchContext,
	PrefetchedDataProps,
	PrefetchedUserDataProps,
	PrefetchUserContext,
} from 'src/shared/prefetch';

import { useCountryCode, useLocale } from 'src/app/shared/hooks/l10n';
import type { MergedState } from 'src/app/shared/hooks/state';
import { useMergedStates } from 'src/app/shared/hooks/state';

import { prefetchHomepagePageDataFromApi, useHomepagePageDataFromApi } from './pageData';
import {
	prefetchHomepageTrendingSectionDataFromApi,
	useHomepageTrendingSectionDataFromApi,
} from './pageTrendingSectionData';
import { prefetchHomepagePageUserDataFromApi, useHomepagePageUserDataFromApi } from './pageUserData';

export type HomepagePagePrefetchedContext = Readonly<{
	pageData: Parameters<typeof useHomepagePageDataFromApi>[0];
	trendingSectionData: Parameters<typeof useHomepageTrendingSectionDataFromApi>[0];
}>;

export type HomepagePagePrefetchedUserContext = NonNullable<Parameters<typeof useHomepagePageUserDataFromApi>[0]>;

export type HomepagePageCombinedState = MergedState<
	ReturnType<typeof useHomepagePageDataFromApi>,
	ReturnType<typeof useHomepageTrendingSectionDataFromApi>,
	ReturnType<typeof useHomepagePageUserDataFromApi>
>;

export function usePageContext(
	prefetchedData?: HomepagePagePrefetchedContext,
	prefetchedUserData?: HomepagePagePrefetchedUserContext,
): HomepagePageCombinedState {
	const locale = useLocale();
	const countryCode = useCountryCode();
	return useMergedStates(
		useHomepagePageDataFromApi(prefetchedData?.pageData, { locale }),
		useHomepageTrendingSectionDataFromApi(prefetchedData?.trendingSectionData, { locale, countryCode }),
		useHomepagePageUserDataFromApi(prefetchedUserData),
	);
}

export async function prefetchPageContext(context: PrefetchContext): Promise<HomepagePagePrefetchedContext> {
	const [pageData, trendingSectionData] = await Promise.all([
		prefetchHomepagePageDataFromApi(context, {
			locale: context.l10n.locale,
		}),
		prefetchHomepageTrendingSectionDataFromApi(context, {
			countryCode: context.l10n.countryCode,
			locale: context.l10n.locale,
		}),
	]);

	return { pageData, trendingSectionData };
}

export async function prefetchPageUserContext(
	context: PrefetchUserContext,
): Promise<HomepagePagePrefetchedUserContext> {
	return prefetchHomepagePageUserDataFromApi(context);
}
export type HomepagePagePrefetchedDataProps = PrefetchedDataProps<HomepagePagePrefetchedContext> &
	PrefetchedUserDataProps<HomepagePagePrefetchedUserContext>;
